import { useMonaco } from '@monaco-editor/react';
import * as lodash from 'lodash';
import { useEffect } from 'react';

import {
  createAvailableWaiveFormulaFieldsProposals,
  createBaseFieldProposals,
  createConcentrationLimitMiscAvailableFieldProposals,
  createEligibilityTestMiscAvailableFieldProposals,
  createFormulaDependencyProposals,
  createIntermediateFieldProposals,
  createMiscConstantFormulaDependencyProposals,
  createMaximumPrefundingAmountAvailableFieldProposals,
  createReceivableAdvanceRateMiscAvailableFieldProposals,
} from '@finley-technologies/calc-agent';
import {
  CreditAgreement,
  PortfolioCovenant,
  Receivable,
} from '@finley-technologies/finley-types';

import { setupMonacoEditorHelpers } from '../ui/common';
import { useCreditAgreementContext } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/hooks/creditAgreementContext';

const { map } = lodash;

const generatePortfolioCovenantInsertText = ({
  key,
  responseProperty,
}: {
  key: string;
  responseProperty: PortfolioCovenant.PortfolioCovenantLoanTapeMetadataResponseProperty;
}) =>
  `loanTapeMetadata.portfolioCovenants${PortfolioCovenant.PORTFOLIO_COVENANT_LOAN_TAPE_METADATA_PARAMS_START_ANCHOR}key${PortfolioCovenant.PORTFOLIO_COVENANT_LOAN_TAPE_METADATA_PARAMS_KEY_VALUE_SEPARATOR}${key}${PortfolioCovenant.PORTFOLIO_COVENANT_LOAN_TAPE_METADATA_PARAMS_KEY_VALUE_DELIMITER}responseProperty${PortfolioCovenant.PORTFOLIO_COVENANT_LOAN_TAPE_METADATA_PARAMS_KEY_VALUE_SEPARATOR}${responseProperty}${PortfolioCovenant.PORTFOLIO_COVENANT_LOAN_TAPE_METADATA_PARAMS_KEY_VALUE_DELIMITER}period${PortfolioCovenant.PORTFOLIO_COVENANT_LOAN_TAPE_METADATA_PARAMS_KEY_VALUE_SEPARATOR}calendarMonth`;

const createPortfolioCovenantProposals = (portfolioCovenantKeys: string[]) =>
  portfolioCovenantKeys.flatMap((key) => [
    {
      label: generatePortfolioCovenantInsertText({
        key,
        responseProperty: 'result',
      }),
      documentation: 'string',
      insertText: generatePortfolioCovenantInsertText({
        key,
        responseProperty: 'result',
      }),
    },
    {
      label: generatePortfolioCovenantInsertText({
        key,
        responseProperty: 'value',
      }),
      documentation: 'string',
      insertText: generatePortfolioCovenantInsertText({
        key,
        responseProperty: 'value',
      }),
    },
  ]);

const createStaticLoanTapeMetadataProposals = () =>
  Receivable.CreditFacility.StaticLoanTapeMetadataProperties.map(
    (property) => ({
      label: `loanTapeMetadata.${property}`,
      documentation: 'string',
      insertText: `loanTapeMetadata.${property}`,
    }),
  );

// TODO: set up separate constants util for our Feature Flags
// Would be good to create some "config" package to be used more widely for flags
type AvailableFlags = 'isExcelEngineAdvanceRateEnabled';

/**
 * *THIS SHOULD ONLY BE CALLED ONCE IN AN APP AS MONACO-EDITOR IS GLOBAL!*
 * Hook to setup `monaco-editor` language configs, including editors for
 * - `Concentration Limit Config` Editor
 * - Formula Editor for `Intermediate Calcs`, `Eligibility Tests`, `Receivable Valuation Rate` and `Receivable Advance Rate`
 *
 * If you need to extend `monaco-editor` for other parts of admin dashboard, consider creating a new hook that takes into account the language ids already used, including:
 *
 * - `json`
 * - `formula-intermediateCalcs`
 * - `formula-receivableRate`
 * - `formula-eligibility`
 */
export const useSetupMonacoEditor = ({
  intermediateCalcs,
  baseFields,
  jsonSchema,
  flags,
  loanTapeMetadataPortfolioCovenantKeys,
}: {
  intermediateCalcs: CreditAgreement.Config.LoanTapeField.IntermediateCalcs.IntermediateCalcContainer[];
  baseFields: CreditAgreement.DataTapeField.BaseConfig[];
  jsonSchema: any;
  flags: { [key in AvailableFlags]?: boolean };
  loanTapeMetadataPortfolioCovenantKeys: string[];
  // @makarand-cpv: pass cpv
}) => {
  const monaco = useMonaco();
  const { orgSlugs } = useCreditAgreementContext();

  useEffect(() => {
    if (monaco) {
      const {
        addMonacoEditorFunctions,
        addMonacoEditorVariables,
      } = setupMonacoEditorHelpers(monaco);

      /**
       * Autocomplete Proposals
       */
      const formulaPrettyPrintProposals = createFormulaDependencyProposals({
        prettyPrint: true,
      });
      const miscFormulaProposals = createMiscConstantFormulaDependencyProposals();
      const baseFieldWithPrefixProposals = createBaseFieldProposals({
        baseFields,
        usePrefix: true,
      });
      const intermediateCalcWithPrefixProposals = createIntermediateFieldProposals(
        {
          intermediateCalcs,
          usePrefix: true,
        },
      );

      const portfolioCovenantProposals = createPortfolioCovenantProposals(
        loanTapeMetadataPortfolioCovenantKeys,
      );

      const staticLoanTapeMetadataProposals = createStaticLoanTapeMetadataProposals();

      /**
       * CashAdvanceRate formulas
       */
      monaco.languages.register({ id: 'formula-cashAdvanceRate' });
      const {
        dispose: disposeCashAdvanceRateCompletionItems,
      } = monaco.languages.registerCompletionItemProvider(
        'formula-cashAdvanceRate',
        {
          provideCompletionItems: (model, position) => {
            const word = model.getWordUntilPosition(position);
            const range = {
              startLineNumber: position.lineNumber,
              endLineNumber: position.lineNumber,
              startColumn: word.startColumn,
              endColumn: word.endColumn,
            };

            // *** Formula Suggestions ***
            const advancedRatePrettyFormulaProposals = createFormulaDependencyProposals(
              { useFullFunctions: true, prettyPrint: true },
            );
            const formulaSuggestions = addMonacoEditorFunctions(
              advancedRatePrettyFormulaProposals,
              {
                range,
                kind: monaco.languages.CompletionItemKind.Function,
                insertTextRules:
                  monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
              },
            );
            const miscFormulaSuggestions = addMonacoEditorFunctions(
              miscFormulaProposals,
              { range, kind: monaco.languages.CompletionItemKind.Constant },
            );

            // *** Variable Suggestions ***
            const baseFieldSuggestions = addMonacoEditorVariables(
              baseFieldWithPrefixProposals,
              { range },
              { includedVariables: 'onlyRange' },
            );
            const intermediateCalcSuggestions = addMonacoEditorVariables(
              intermediateCalcWithPrefixProposals,
              { range },
              { includedVariables: 'onlyRange' },
            );
            const receivableAdvanceRateMiscSuggestions = addMonacoEditorVariables(
              createReceivableAdvanceRateMiscAvailableFieldProposals(),
              { range },
              { includedVariables: 'onlyRange' },
            );

            return {
              suggestions: [
                ...formulaSuggestions,
                ...miscFormulaSuggestions,
                ...baseFieldSuggestions,
                ...intermediateCalcSuggestions,
                ...receivableAdvanceRateMiscSuggestions,
              ],
            };
          },
        },
      );

      /**
       * Concentration Limit Configs
       */

      monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
        validate: true,
        schemaValidation: 'warning',
        schemas: [
          {
            uri: 'concentrationLimitConfig',
            fileMatch: ['*'],
            schema: jsonSchema,
          },
        ],
      });
      const {
        dispose: disposeConcentrationLimitCompletionItems,
      } = monaco.languages.registerCompletionItemProvider('json', {
        provideCompletionItems: (model, position) => {
          const concentrationLimitMiscFieldProposals = createConcentrationLimitMiscAvailableFieldProposals();

          const formulaProposals = createFormulaDependencyProposals();
          const textUntilPosition = model.getValueInRange({
            startLineNumber: position.lineNumber,
            startColumn: 1,
            endLineNumber: position.lineNumber,
            endColumn: position.column,
          });
          const matchFormula = textUntilPosition.match(
            /(?<=.*"formula":\s*)(.*)(?=.*)/,
          );
          const matchField = textUntilPosition.match(
            /(?<=.*"field":\s*)(.*)(?=.*)/,
          );
          const word = model.getWordUntilPosition(position);
          const range = {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: word.startColumn,
            endColumn: word.endColumn,
          };
          if (matchFormula) {
            // *** Formula Suggestions ***
            const formulaSuggestions = addMonacoEditorFunctions(
              formulaProposals,
              {
                range,
                kind: monaco.languages.CompletionItemKind.Function,
                insertTextRules:
                  monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
              },
            );
            const miscFormulaSuggestions = addMonacoEditorFunctions(
              miscFormulaProposals,
              { range, kind: monaco.languages.CompletionItemKind.Constant },
            );

            // *** Variable Suggestions ***
            const fieldSuggestions = addMonacoEditorVariables(
              createAvailableWaiveFormulaFieldsProposals(),
              { range },
            );

            return {
              suggestions: [
                ...formulaSuggestions,
                ...miscFormulaSuggestions,
                ...fieldSuggestions,
              ],
            };
          }
          if (matchField) {
            // *** Formula Suggestions ***
            const fieldSuggestions = addMonacoEditorFunctions(
              [
                ...baseFieldWithPrefixProposals,
                ...intermediateCalcWithPrefixProposals,
                ...concentrationLimitMiscFieldProposals,
              ],
              { kind: monaco.languages.CompletionItemKind.Variable, range },
            );

            return {
              suggestions: fieldSuggestions,
            };
          }

          return {
            suggestions: [],
          };
        },
      });

      /**
       * Intermediate Calc formulas
       */
      monaco.languages.register({ id: 'formula-intermediateCalcs' });
      const {
        dispose: disposeIntermediateCalcCompletionItems,
      } = monaco.languages.registerCompletionItemProvider(
        'formula-intermediateCalcs',
        {
          provideCompletionItems: (model, position) => {
            const word = model.getWordUntilPosition(position);
            const range = {
              startLineNumber: position.lineNumber,
              endLineNumber: position.lineNumber,
              startColumn: word.startColumn,
              endColumn: word.endColumn,
            };

            // *** Formula Suggestions ***
            const formulaSuggestions = addMonacoEditorFunctions(
              formulaPrettyPrintProposals,
              {
                range,
                kind: monaco.languages.CompletionItemKind.Function,
                insertTextRules:
                  monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
              },
            );
            const miscFormulaSuggestions = addMonacoEditorFunctions(
              miscFormulaProposals,
              { range, kind: monaco.languages.CompletionItemKind.Constant },
            );

            // *** Variable Suggestions ***
            const intermediateCalcSuggestions = addMonacoEditorVariables(
              createIntermediateFieldProposals({
                intermediateCalcs,
                usePrefix: false,
              }),
              { range },
            );
            const baseFieldSuggestions = addMonacoEditorVariables(
              baseFieldWithPrefixProposals,
              { range },
            );
            const orgSlugProposals = map(orgSlugs, (slug) => ({
              label: slug,
              documentation: 'string',
              insertText: slug,
            }));
            const orgSlugSuggestions = addMonacoEditorVariables(
              orgSlugProposals,
              { range },
            );
            const portfolioCovenantSuggestions = addMonacoEditorVariables(
              portfolioCovenantProposals,
              { range },
              { includedVariables: 'onlyRow' },
            );
            const staticLoanTapeMetadataSuggestions = addMonacoEditorVariables(
              staticLoanTapeMetadataProposals,
              { range },
              { includedVariables: 'onlyRow' },
            );

            return {
              suggestions: [
                ...formulaSuggestions,
                ...miscFormulaSuggestions,
                ...baseFieldSuggestions,
                ...intermediateCalcSuggestions,
                ...orgSlugSuggestions,
                ...portfolioCovenantSuggestions,
                ...staticLoanTapeMetadataSuggestions,
              ],
            };
          },
        },
      );

      /**
       * Eligibility Test formulas
       */
      monaco.languages.register({ id: 'formula-eligibilityTest' });
      const {
        dispose: disposeEligibilityTestCompletionItems,
      } = monaco.languages.registerCompletionItemProvider(
        'formula-eligibilityTest',
        {
          provideCompletionItems: (model, position) => {
            const word = model.getWordUntilPosition(position);
            const range = {
              startLineNumber: position.lineNumber,
              endLineNumber: position.lineNumber,
              startColumn: word.startColumn,
              endColumn: word.endColumn,
            };

            // *** Formula Suggestions ***
            const formulaSuggestions = addMonacoEditorFunctions(
              formulaPrettyPrintProposals,
              {
                range,
                kind: monaco.languages.CompletionItemKind.Function,
                insertTextRules:
                  monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
              },
            );
            const miscFormulaSuggestions = addMonacoEditorFunctions(
              miscFormulaProposals,
              { range, kind: monaco.languages.CompletionItemKind.Constant },
            );

            // *** Variable Suggestions ***
            const eligibilityTestMiscFormulaSuggestions = addMonacoEditorVariables(
              createEligibilityTestMiscAvailableFieldProposals(),
              { range },
            );
            const intermediateCalcSuggestions = addMonacoEditorVariables(
              intermediateCalcWithPrefixProposals,
              { range },
            );
            const baseFieldSuggestions = addMonacoEditorVariables(
              baseFieldWithPrefixProposals,
              { range },
            );

            return {
              suggestions: [
                ...formulaSuggestions,
                ...miscFormulaSuggestions,
                ...baseFieldSuggestions,
                ...intermediateCalcSuggestions,
                ...eligibilityTestMiscFormulaSuggestions,
              ],
            };
          },
        },
      );

      /**
       * Max Prefunding Amount Formulas
       */
      monaco.languages.register({ id: 'formula-maximumPrefundingAmount' });
      const {
        dispose: disposeMaximumPrefundingAmountCompletionItems,
      } = monaco.languages.registerCompletionItemProvider(
        'formula-maximumPrefundingAmount',
        {
          provideCompletionItems: (model, position) => {
            const word = model.getWordUntilPosition(position);
            const range = {
              startLineNumber: position.lineNumber,
              endLineNumber: position.lineNumber,
              startColumn: word.startColumn,
              endColumn: word.endColumn,
            };

            // *** Formula Suggestions ***
            const formulaSuggestions = addMonacoEditorFunctions(
              formulaPrettyPrintProposals,
              {
                range,
                kind: monaco.languages.CompletionItemKind.Function,
                insertTextRules:
                  monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
              },
            );
            const miscFormulaSuggestions = addMonacoEditorFunctions(
              miscFormulaProposals,
              { range, kind: monaco.languages.CompletionItemKind.Constant },
            );

            // *** Variable Suggestions ***
            const maximumPrefundingAmountSuggestions = addMonacoEditorVariables(
              createMaximumPrefundingAmountAvailableFieldProposals(),
              { range },
              { includedVariables: 'onlyRow' },
            );

            return {
              suggestions: [
                ...formulaSuggestions,
                ...miscFormulaSuggestions,
                ...maximumPrefundingAmountSuggestions,
              ],
            };
          },
        },
      );

      /**
       * ReceivableAdvanceRate formulas
       */
      // TODO: create global monaco ID constant variables to replace each hardcoded ID
      // This will also help alleviate and manage potential clashes (currently won't be able to tell)
      monaco.languages.register({ id: 'formula-receivableAdvanceRate' });
      const {
        dispose: disposeReceivableAdvanceRateCompletionItems,
      } = monaco.languages.registerCompletionItemProvider(
        'formula-receivableAdvanceRate',
        {
          provideCompletionItems: (model, position) => {
            const word = model.getWordUntilPosition(position);
            const range = {
              startLineNumber: position.lineNumber,
              endLineNumber: position.lineNumber,
              startColumn: word.startColumn,
              endColumn: word.endColumn,
            };

            // *** Formula Suggestions ***
            const advancedRatePrettyFormulaProposals = createFormulaDependencyProposals(
              {
                useFullFunctions: flags.isExcelEngineAdvanceRateEnabled,
                prettyPrint: true,
              },
            );
            const formulaSuggestions = addMonacoEditorFunctions(
              advancedRatePrettyFormulaProposals,
              {
                range,
                kind: monaco.languages.CompletionItemKind.Function,
                insertTextRules:
                  monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
              },
            );
            const miscFormulaSuggestions = addMonacoEditorFunctions(
              miscFormulaProposals,
              { range, kind: monaco.languages.CompletionItemKind.Constant },
            );

            // *** Variable Suggestions ***
            const baseFieldSuggestions = addMonacoEditorVariables(
              baseFieldWithPrefixProposals,
              { range },
              {
                includedVariables: flags.isExcelEngineAdvanceRateEnabled
                  ? 'all'
                  : 'onlyRow',
              },
            );
            const intermediateCalcSuggestions = addMonacoEditorVariables(
              intermediateCalcWithPrefixProposals,
              { range },
              {
                includedVariables: flags.isExcelEngineAdvanceRateEnabled
                  ? 'all'
                  : 'onlyRow',
              },
            );
            const receivableAdvanceRateMiscSuggestions = addMonacoEditorVariables(
              createReceivableAdvanceRateMiscAvailableFieldProposals(),
              { range },
              {
                includedVariables: flags.isExcelEngineAdvanceRateEnabled
                  ? 'all'
                  : 'onlyRow',
              },
            );

            return {
              suggestions: [
                ...formulaSuggestions,
                ...miscFormulaSuggestions,
                ...baseFieldSuggestions,
                ...intermediateCalcSuggestions,
                ...receivableAdvanceRateMiscSuggestions,
              ],
            };
          },
        },
      );

      /**
       * ReceivableValuationRate formulas
       */
      monaco.languages.register({ id: 'formula-receivableValuationRate' });
      const {
        dispose: disposeReceivableValuationRateCompletionItems,
      } = monaco.languages.registerCompletionItemProvider(
        'formula-receivableValuationRate',
        {
          provideCompletionItems: (model, position) => {
            const word = model.getWordUntilPosition(position);
            const range = {
              startLineNumber: position.lineNumber,
              endLineNumber: position.lineNumber,
              startColumn: word.startColumn,
              endColumn: word.endColumn,
            };

            // *** Formula Suggestions ***
            const formulaSuggestions = addMonacoEditorFunctions(
              formulaPrettyPrintProposals,
              {
                range,
                kind: monaco.languages.CompletionItemKind.Function,
                insertTextRules:
                  monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
              },
            );
            const miscFormulaSuggestions = addMonacoEditorFunctions(
              miscFormulaProposals,
              { range, kind: monaco.languages.CompletionItemKind.Constant },
            );

            // *** Variable Suggestions ***
            const intermediateCalcSuggestions = addMonacoEditorVariables(
              intermediateCalcWithPrefixProposals,
              { range },
            );
            const baseFieldSuggestions = addMonacoEditorVariables(
              baseFieldWithPrefixProposals,
              { range },
            );

            return {
              suggestions: [
                ...formulaSuggestions,
                ...miscFormulaSuggestions,
                ...baseFieldSuggestions,
                ...intermediateCalcSuggestions,
              ],
            };
          },
        },
      );

      /**
       * PurchasePercentageRate formulas
       */
      monaco.languages.register({ id: 'formula-purchasePercentageRate' });
      const {
        dispose: disposePurchasePercentageRateCompletionItems,
      } = monaco.languages.registerCompletionItemProvider(
        'formula-purchasePercentageRate',
        {
          provideCompletionItems: (model, position) => {
            const word = model.getWordUntilPosition(position);
            const range = {
              startLineNumber: position.lineNumber,
              endLineNumber: position.lineNumber,
              startColumn: word.startColumn,
              endColumn: word.endColumn,
            };

            // *** Formula Suggestions ***
            const formulaSuggestions = addMonacoEditorFunctions(
              formulaPrettyPrintProposals,
              {
                range,
                kind: monaco.languages.CompletionItemKind.Function,
                insertTextRules:
                  monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
              },
            );
            const miscFormulaSuggestions = addMonacoEditorFunctions(
              miscFormulaProposals,
              { range, kind: monaco.languages.CompletionItemKind.Constant },
            );

            // *** Variable Suggestions ***
            const intermediateCalcSuggestions = addMonacoEditorVariables(
              intermediateCalcWithPrefixProposals,
              { range },
            );
            const baseFieldSuggestions = addMonacoEditorVariables(
              baseFieldWithPrefixProposals,
              { range },
            );

            return {
              suggestions: [
                ...formulaSuggestions,
                ...miscFormulaSuggestions,
                ...baseFieldSuggestions,
                ...intermediateCalcSuggestions,
              ],
            };
          },
        },
      );

      /**
       * *** Class Details: Attachment Rate Limit Formula ***
       * Default run "ExcelEngine" and support aggregate (range) formulas.
       * Will not include non-range formulas since attachment rate limit
       * formulas currently don't support receivable-level fields
       */
      monaco.languages.register({ id: 'formula-attachmentRateLimit' });
      const {
        dispose: disposeAttachmentRateLimitCompletionItems,
      } = monaco.languages.registerCompletionItemProvider(
        'formula-attachmentRateLimit',
        {
          provideCompletionItems: (model, position) => {
            const word = model.getWordUntilPosition(position);
            const range = {
              startLineNumber: position.lineNumber,
              endLineNumber: position.lineNumber,
              startColumn: word.startColumn,
              endColumn: word.endColumn,
            };

            // *** Formula Suggestions ***
            const advancedRatePrettyFormulaProposals = createFormulaDependencyProposals(
              { useFullFunctions: true, prettyPrint: true },
            );
            const formulaSuggestions = addMonacoEditorFunctions(
              advancedRatePrettyFormulaProposals,
              {
                range,
                kind: monaco.languages.CompletionItemKind.Function,
                insertTextRules:
                  monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
              },
            );
            const miscFormulaSuggestions = addMonacoEditorFunctions(
              miscFormulaProposals,
              { range, kind: monaco.languages.CompletionItemKind.Constant },
            );

            // *** Variable Suggestions ***
            const baseFieldSuggestions = addMonacoEditorVariables(
              baseFieldWithPrefixProposals,
              { range },
              { includedVariables: 'onlyRange' },
            );
            const intermediateCalcSuggestions = addMonacoEditorVariables(
              intermediateCalcWithPrefixProposals,
              { range },
              { includedVariables: 'onlyRange' },
            );
            const receivableAdvanceRateMiscSuggestions = addMonacoEditorVariables(
              createReceivableAdvanceRateMiscAvailableFieldProposals(),
              { range },
              { includedVariables: 'onlyRange' },
            );

            return {
              suggestions: [
                ...formulaSuggestions,
                ...miscFormulaSuggestions,
                ...baseFieldSuggestions,
                ...intermediateCalcSuggestions,
                ...receivableAdvanceRateMiscSuggestions,
              ],
            };
          },
        },
      );

      return () => {
        disposeCashAdvanceRateCompletionItems();
        disposeConcentrationLimitCompletionItems();
        disposeIntermediateCalcCompletionItems();
        disposeEligibilityTestCompletionItems();
        disposeMaximumPrefundingAmountCompletionItems();
        disposeReceivableAdvanceRateCompletionItems();
        disposeReceivableValuationRateCompletionItems();
        disposeAttachmentRateLimitCompletionItems();
        disposePurchasePercentageRateCompletionItems();
      };
    }
    return () => {};
  }, [
    monaco,
    orgSlugs,
    baseFields,
    intermediateCalcs,
    jsonSchema,
    flags,
    loanTapeMetadataPortfolioCovenantKeys,
  ]);
};
